import React, { useMemo, useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Divider,
} from '@mui/material';
import { doc, setDoc, updateDoc, deleteDoc, serverTimestamp, getDoc } from 'firebase/firestore';
import { getBytes, ref } from 'firebase/storage';
import { db, storage } from '../firebaseConfig';
import { canAccessDevRulesTest } from '../utils/devRulesTestAccess';

const DevRulesTest = ({ user, userData }) => {
  const canAccess = canAccessDevRulesTest(user, userData);
  const [farmId, setFarmId] = useState(userData?.activeFarmId || '');
  const [otherFarmId, setOtherFarmId] = useState('');
  const [logoPath, setLogoPath] = useState('');
  const [invoicePath, setInvoicePath] = useState('');
  const [results, setResults] = useState([]);

  const uid = user?.uid || '';

  const appendResult = (label, passed, details = '') => {
    setResults((prev) => [
      {
        ts: new Date().toISOString(),
        label,
        passed,
        details,
      },
      ...prev,
    ]);
  };

  const getMembership = async (targetFarmId) => {
    if (!uid || !targetFarmId) return null;
    const snap = await getDoc(doc(db, 'farms', targetFarmId, 'members', uid));
    return snap.exists() ? (snap.data() || {}) : null;
  };
  const nonMemberFarmId = useMemo(() => otherFarmId.trim(), [otherFarmId]);
  const activeFarmId = useMemo(() => farmId.trim(), [farmId]);

  if (!canAccess) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="h5">Dev Rules Test</Typography>
        <Typography sx={{ mt: 1 }}>Not available in this environment/account.</Typography>
      </Box>
    );
  }

  const testCrossFarmOwnerInjection = async () => {
    if (!uid || !nonMemberFarmId) {
      appendResult('Cross-farm owner membership create', false, 'Missing uid or target farm id.');
      return;
    }
    const existingMembership = await getMembership(nonMemberFarmId);
    if (existingMembership) {
      appendResult(
        'Cross-farm owner membership create',
        false,
        'Invalid test setup: you are already a member of this target farm. Pick a true non-member farm id.'
      );
      return;
    }
    try {
      const memberRef = doc(db, 'farms', nonMemberFarmId, 'members', uid);
      await setDoc(
        memberRef,
        {
          role: 'owner',
          status: 'active',
          displayName: userData?.displayName || '',
          touchedAt: serverTimestamp(),
        },
        { merge: true }
      );
      const verifySnap = await getDoc(memberRef);
      if (!verifySnap.exists()) {
        appendResult(
          'Cross-farm owner membership create',
          false,
          `Write returned success but doc not found at farms/${nonMemberFarmId}/members/${uid}.`
        );
        return;
      }
      appendResult(
        'Cross-farm owner membership create',
        false,
        `Unexpectedly succeeded at farms/${nonMemberFarmId}/members/${uid}.`
      );
    } catch (err) {
      appendResult('Cross-farm owner membership create', true, err?.code || err?.message || 'Denied as expected.');
    }
  };

  const testSelfRoleEscalation = async () => {
    if (!uid || !activeFarmId) {
      appendResult('Self membership role/status update', false, 'Missing uid or active farm id.');
      return;
    }
    const membership = await getMembership(activeFarmId);
    const role = String(membership?.role || '').toLowerCase();
    if (role === 'owner' || role === 'admin') {
      appendResult(
        'Self membership role/status update',
        false,
        'Invalid test setup: current account is owner/admin, so role updates are expected to be allowed.'
      );
      return;
    }
    try {
      await updateDoc(doc(db, 'farms', activeFarmId, 'members', uid), {
        role: 'owner',
        status: 'active',
      });
      appendResult('Self membership role/status update', false, 'Unexpectedly succeeded.');
    } catch (err) {
      appendResult('Self membership role/status update', true, err?.code || err?.message || 'Denied as expected.');
    }
  };

  const testSelfLeave = async () => {
    if (!uid || !activeFarmId) {
      appendResult('Self leave membership delete', false, 'Missing uid or active farm id.');
      return;
    }
    try {
      const memberRef = doc(db, 'farms', activeFarmId, 'members', uid);
      const farmRef = doc(db, 'farms', activeFarmId);
      const [memberSnap, farmSnap] = await Promise.all([getDoc(memberRef), getDoc(farmRef)]);
      const memberData = memberSnap.exists() ? (memberSnap.data() || {}) : null;
      const farmData = farmSnap.exists() ? (farmSnap.data() || {}) : null;

      const canAutoRestore =
        Boolean(memberData)
        && Boolean(farmData)
        && String(farmData?.createdBy || '') === uid
        && String(memberData?.role || '').toLowerCase() === 'owner';

      await deleteDoc(doc(db, 'farms', activeFarmId, 'members', uid));

      if (canAutoRestore) {
        await setDoc(
          memberRef,
          {
            uid,
            role: 'owner',
            status: 'active',
            displayName: memberData.displayName || userData?.displayName || '',
            photoURL: memberData.photoURL || userData?.photoURL || '',
            joinedAt: memberData.joinedAt || serverTimestamp(),
          },
          { merge: true }
        );
        appendResult(
          'Self leave membership delete',
          true,
          'Delete allowed, then auto-restored owner membership for this created farm.'
        );
        return;
      }

      appendResult(
        'Self leave membership delete',
        true,
        'Delete allowed. No auto-restore was performed for this farm.'
      );
    } catch (err) {
      appendResult('Self leave membership delete', false, err?.code || err?.message || 'Denied.');
    }
  };

  const extractFarmIdFromStoragePath = (pathValue) => {
    const cleaned = String(pathValue || '').trim().replace(/^\/+/, '');
    const parts = cleaned.split('/');
    if (parts.length < 4) return '';
    if (parts[0] !== 'farms') return '';
    return parts[1] || '';
  };
  const testStorageRead = async (label, pathValue) => {
    const target = pathValue.trim();
    if (!target) {
      appendResult(label, false, 'Missing storage path.');
      return;
    }
    const targetFarmId = extractFarmIdFromStoragePath(target);
    if (!targetFarmId) {
      appendResult(label, false, 'Invalid storage path. Expected farms/<farmId>/(logos|invoices)/<file>.');
      return;
    }
    const membership = await getMembership(targetFarmId);
    if (membership) {
      appendResult(
        label,
        false,
        `Invalid test setup: you are an active member of farm ${targetFarmId}, so read may be allowed.`
      );
      return;
    }
    try {
      // Read a small byte range to validate Storage rules directly.
      await getBytes(ref(storage, target), 64);
      appendResult(label, false, 'Unexpectedly succeeded.');
    } catch (err) {
      const code = String(err?.code || '');
      if (code === 'storage/object-not-found') {
        appendResult(label, false, 'Object not found. Pick an existing file in a farm you are not a member of.');
        return;
      }
      if (code === 'storage/unauthorized') {
        appendResult(label, true, code);
        return;
      }
      appendResult(label, false, err?.code || err?.message || 'Unexpected storage error.');
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h4">Dev Rules Test</Typography>
      <Typography sx={{ mt: 1, color: 'text.secondary' }}>
        Use this page to run direct security-rule checks from the client SDK.
      </Typography>

      <Paper sx={{ mt: 2, p: 2 }}>
        <Typography variant="h6">Input</Typography>
        <TextField
          sx={{ mt: 1 }}
          fullWidth
          label="Active Farm Id (for self tests)"
          value={farmId}
          onChange={(e) => setFarmId(e.target.value)}
        />
        <TextField
          sx={{ mt: 1 }}
          fullWidth
          label="Other Farm Id (non-member target)"
          value={otherFarmId}
          onChange={(e) => setOtherFarmId(e.target.value)}
        />
        <TextField
          sx={{ mt: 1 }}
          fullWidth
          label="Logo storage path to test read deny (e.g. farms/<farmId>/logos/logo-123.png)"
          value={logoPath}
          onChange={(e) => setLogoPath(e.target.value)}
        />
        <TextField
          sx={{ mt: 1 }}
          fullWidth
          label="Invoice storage path to test read deny (e.g. farms/<farmId>/invoices/<order>.pdf)"
          value={invoicePath}
          onChange={(e) => setInvoicePath(e.target.value)}
        />
      </Paper>

      <Paper sx={{ mt: 2, p: 2, display: 'grid', gap: 1 }}>
        <Typography variant="h6">Tests</Typography>
        <Button variant="contained" onClick={testCrossFarmOwnerInjection}>
          Test Cross-Farm Owner Membership Create (should fail)
        </Button>
        <Button variant="contained" onClick={testSelfRoleEscalation}>
          Test Self Membership Role/Status Update (should fail)
        </Button>
        <Button variant="outlined" color="warning" onClick={testSelfLeave}>
          Test Self Leave Membership Delete (dangerous)
        </Button>
        <Button variant="contained" onClick={() => testStorageRead('Logo read test', logoPath)}>
          Test Logo Read (should fail when non-member)
        </Button>
        <Button variant="contained" onClick={() => testStorageRead('Invoice read test', invoicePath)}>
          Test Invoice Read (should fail when non-member)
        </Button>
      </Paper>

      <Paper sx={{ mt: 2, p: 2 }}>
        <Typography variant="h6">Results</Typography>
        <Divider sx={{ my: 1 }} />
        {results.length === 0 ? (
          <Typography color="text.secondary">No tests run yet.</Typography>
        ) : (
          results.map((r, idx) => (
            <Box key={`${r.ts}-${idx}`} sx={{ mb: 1 }}>
              <Typography sx={{ fontWeight: 600, color: r.passed ? 'success.main' : 'error.main' }}>
                {r.passed ? 'PASS' : 'FAIL'} - {r.label}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {r.details}
              </Typography>
            </Box>
          ))
        )}
      </Paper>
    </Box>
  );
};

export default DevRulesTest;
